// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Building2 } from "lucide-react"
import { resellersApiService } from "@crm-src/lib/resellers-api"

interface Entity {
  id: string | number
  name?: string
  company?: string
}

interface Reseller {
  id: number | string
  name: string
  code?: string
}

interface BulkAssignResellerModalProps {
  isOpen: boolean
  onClose: () => void
  selectedEntities: Entity[]
  entityType: 'customer' | 'prospect'
  onConfirmAssign?: (entityIds: string[], resellerId: string) => Promise<void>
}

export function BulkAssignResellerModal({
  isOpen,
  onClose,
  selectedEntities,
  entityType,
  onConfirmAssign
}: BulkAssignResellerModalProps) {
  const { toast } = useToast()
  const [isAssigning, setIsAssigning] = useState(false)
  const [selectedResellerId, setSelectedResellerId] = useState<string>("")
  const [resellers, setResellers] = useState<Reseller[]>([])
  const [loadingResellers, setLoadingResellers] = useState(false)

  // Load resellers when modal opens
  useEffect(() => {
    if (isOpen) {
      loadResellers()
    }
  }, [isOpen])

  const loadResellers = async () => {
    setLoadingResellers(true)
    try {
      const data = await resellersApiService.getAll()
      setResellers(data.resellers.sort((a, b) => (a.code || '').localeCompare(b.code || '')))
    } catch (error) {
      console.error('Failed to load resellers:', error)
      toast({
        title: "Error",
        description: "Failed to load resellers",
        variant: "destructive",
      })
    } finally {
      setLoadingResellers(false)
    }
  }

  const handleAssign = async () => {
    if (!onConfirmAssign || selectedEntities.length === 0) return

    if (!selectedResellerId) {
      toast({
        title: "Error",
        description: "Please select a reseller",
        variant: "destructive",
      })
      return
    }

    setIsAssigning(true)
    try {
      const entityIds = selectedEntities.map(entity => entity.id.toString())
      await onConfirmAssign(entityIds, selectedResellerId)
      setSelectedResellerId("")
      onClose()
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsAssigning(false)
    }
  }

  const handleClose = () => {
    if (!isAssigning) {
      setSelectedResellerId("")
      onClose()
    }
  }

  if (!isOpen || selectedEntities.length === 0) return null

  const entityLabel = entityType === 'customer' ? 'Customer' : 'Prospect'
  const entityLabelPlural = entityType === 'customer' ? 'Customers' : 'Prospects'

  const sortedResellers = [...resellers].sort((a, b) => {
    // Sort by code numerically if both have codes
    if (a.code && b.code) {
      return parseInt(a.code) - parseInt(b.code)
    }
    // Resellers with codes come before those without
    if (a.code && !b.code) return -1
    if (!a.code && b.code) return 1
    // If neither has code, sort by name
    return a.name.localeCompare(b.name)
  })

  return (
    <Dialog open={isOpen} onClose={() => !isAssigning && handleClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              height: 40,
              width: 40,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              color: "primary.main",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            }}
          >
            <Building2 size={20} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "text.primary" }}>
              Assign Reseller to {selectedEntities.length} {selectedEntities.length > 1 ? entityLabelPlural : entityLabel}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.disabled", mt: 0.25 }}>
              Select a reseller to assign to the selected {entityLabelPlural.toLowerCase()}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              border: 1,
              borderColor: (t) => alpha(t.palette.primary.main, 0.3),
              borderRadius: 1,
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Box sx={{ color: "primary.main", mt: 0.25, flexShrink: 0, display: "flex" }}>
                <Building2 size={20} />
              </Box>
              <Box>
                <Typography component="h4" sx={{ fontSize: 14, fontWeight: 500, color: "primary.dark", mb: 0.5 }}>
                  Bulk Reseller Assignment
                </Typography>
                <Typography sx={{ fontSize: 14, color: "primary.main", mb: 1 }}>
                  The selected reseller will be assigned to all {selectedEntities.length} selected
                  {' '}{entityLabelPlural.toLowerCase()}. Existing reseller assignments will be replaced.
                </Typography>
                {selectedEntities.length <= 5 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 12, color: "primary.main", fontWeight: 500, mb: 0.5 }}>{entityLabelPlural}:</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 0.5, fontSize: 12, color: "primary.main" }}>
                      {selectedEntities.map((entity) => (
                        <Box component="li" key={entity.id} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          • {entity.name || entity.company || `${entityLabel} ${entity.id}`}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box>
            <FormLabel sx={{ display: "block", mb: 1, fontSize: 14, fontWeight: 500, color: "text.primary" }}>
              Select Reseller
            </FormLabel>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedResellerId}
              onChange={(e) => setSelectedResellerId(e.target.value)}
              disabled={loadingResellers || isAssigning}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  const selected = value as string
                  if (!selected) {
                    return <Box component="span" sx={{ color: "text.disabled" }}>{loadingResellers ? "Loading resellers..." : "Select a reseller..."}</Box>
                  }
                  if (selected === "none") {
                    return <Box component="span" sx={{ color: "text.primary" }}>None (Direct Sales)</Box>
                  }
                  const reseller = resellers.find(r => r.id.toString() === selected)
                  return reseller ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                      <Box component="span">{reseller.name}</Box>
                      {reseller.code && (
                        <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: "secondary.main" }}>
                          {reseller.code}
                        </Box>
                      )}
                    </Box>
                  ) : null
                },
              }}
            >
              <MenuItem value="none">
                <Box component="span" sx={{ color: "text.primary" }}>None (Direct Sales)</Box>
              </MenuItem>
              {sortedResellers.map((reseller) => (
                <MenuItem key={reseller.id} value={reseller.id.toString()}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 2 }}>
                    <Box component="span" sx={{ color: "text.primary" }}>{reseller.name}</Box>
                    {reseller.code && (
                      <Box component="span" sx={{ ml: 2, fontSize: "0.875rem", fontWeight: 600, color: "secondary.main" }}>
                        {reseller.code}
                      </Box>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: 12, color: "text.disabled", mt: 1 }}>
              {selectedResellerId
                ? selectedResellerId === "none" ? "Direct Sales (No Reseller)" : "1 reseller selected"
                : 'No reseller selected yet'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={isAssigning}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleAssign}
          disabled={isAssigning || !selectedResellerId || loadingResellers}
          sx={{ minWidth: 120 }}
          startIcon={
            isAssigning
              ? <CircularProgress size={16} color="inherit" />
              : <Building2 size={16} />
          }
        >
          {isAssigning ? "Assigning..." : "Assign Reseller"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
